import React, { Fragment, useState, useEffect } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { compose } from "redux";
import { bool, shape, string } from "prop-types";
import { push } from "connected-react-router";
import { isEmpty } from "lodash";
import * as Yup from "yup";
import { Spring, config } from "react-spring";
import { useLocation, useParams } from "react-router-dom";
import { Col, Row } from "react-bootstrap";
import { withFormik, Form, Field } from "formik";
import moment from "moment";
import * as i18n from "util/i18n";
import * as numberUtils from "util/number";
import { transactionStatus } from "pages/transactions/constants";
import { actions as loansActions, selectors as loansSelectors } from "reducers/loans";
import { actions as formActions, selectors as formSelectors } from "reducers/form";
import MainContainer from "pages/_components/MainContainer";
import GridTable from "pages/_components/GridTable/GridTable";
import CustomData from "pages/_components/CustomData/CustomData";
import CustomText from "pages/_components/CustomText/CustomText";
import Credential from "pages/_components/fields/credentials/Credential";
import FormattedAmount from "pages/_components/FormattedAmount";
import CustomButton from "pages/_components/CustomButton/CustomButton";
import Button from "pages/_components/Button";
import useReactPrint from "pages/_components/Print/useReactToPrint";
import Image from "pages/_components/Image";
import Notification from "pages/_components/Notification";
import I18n from "pages/_components/I18n";
import { EcheqTransactionCard } from "pages/_components/EcheqTransactionCard";
import {
    TransactionCard,
    TransactionHeader,
    TransactionInfo,
    TransactionSign,
} from "pages/_components/TransactionCard";
import { ResponsiveHeader } from "pages/_components/header";

const UnsecuredLoanTransactionPage = ({ isDesktop, transaction, fetching, isCancellingTransaction }) => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const { pathname, state = {} } = useLocation();
    const [showMoreData, setShowMoreData] = useState(!isDesktop);
    const [showInstallments, setShowInstallments] = useState(!isDesktop);
    const [showToken, setShowToken] = useState(false);
    const [buttonLabel, setButtonLabel] = useState("echecks.seeMore.button");

    const isBackoffice = pathname.includes("backoffice");
    const transactionData = {
        loanNumber: transaction?.data?.loanNumber || "",
        operationId: transaction?.data?.operationId || "",
        amount: transaction?.data?.amount || "",
        loanSimulation: transaction?.data?.loanSimulation || "",
    };
    const isLoading = fetching || !transaction || !transaction?.data || !transaction?.data?.amount;

    const { user } = useSelector((_state) => _state.session);

    const isPending = transaction.idTransactionStatus === transactionStatus.PENDING;

    const showSignButton =
        isEmpty(transaction?.signatures?.find((sign) => sign.idUser === user?.userId)) &&
        transaction.idTransactionStatus === transactionStatus.PENDING;

    useEffect(() => {
        if (id && isEmpty(state?.transaction)) {
            dispatch(formActions.readTransactionTicket(id));
        }
    }, [id]);

    useEffect(() => {
        if (!transaction?.data?.amount && isEmpty(state?.transaction)) {
            dispatch(formActions.readTransactionTicket(id));
        }
    }, [transaction]);

    const handleSeeMoreData = () => {
        setShowMoreData(!showMoreData);
        if (!showMoreData) {
            setButtonLabel("unsecured.loan.seeLess.ticket.button");
        } else {
            setButtonLabel("unsecured.loan.seeMore.ticket.button");
        }
    };

    const handleSeeInstallment = () => {
        setShowInstallments(!showInstallments);
    };

    const { componentRef, handlePrint, mobileRef, handleShareMobile } = useReactPrint(
        "activities.unsecured.loans.application.send",
        id,
        "unsecured.loan",
    );

    const handleDownload = async () => {
        await setShowMoreData(true);
        return handlePrint();
    };

    const sign = () =>
        !showToken && !isCancellingTransaction ? (
            <>
                <Col xs={12} md={4} mdOffset={2}>
                    <CustomButton
                        func={() => dispatch(formActions.changeCancelTransaction(true))}
                        text="forms.cancelTransaction.link"
                        textType="I18N"
                        className="btn btn-primary"
                    />
                </Col>
                <Col xs={12} md={4}>
                    <CustomButton
                        func={() => setShowToken(true)}
                        text="forms.signTransaction.link"
                        textType="I18N"
                        className="btn btn-primary"
                    />
                </Col>
            </>
        ) : (
            <Form className="above-the-fold container--layout">
                <Row>
                    <Col xs={12} md={4} mdOffset={2}>
                        {isCancellingTransaction ? (
                            <Field
                                idForm="form.credential"
                                name="password"
                                labelText="form.credential.password.label"
                                placeholderText="Ej: Ju@n_123"
                                component={Credential}
                                type="password"
                            />
                        ) : (
                            <Field
                                component={Credential}
                                idForm="form.credential"
                                name="credential"
                                type="otp"
                                labelText="form.credential.token.label"
                                placeholderText="Ej: 120573"
                            />
                        )}
                    </Col>
                    <Col xs={12} md={4} className="align-items-end">
                        <div className="form-group hidden-label">
                            <div className="button-group">
                                <Button block type="submit" label="global.send" bsStyle="primary" loading={fetching} />
                            </div>
                        </div>
                    </Col>
                </Row>
            </Form>
        );

    const transactionStatusID = () => {
        let statusId;
        switch (transaction?.idTransactionStatus) {
            case transactionStatus.PENDING:
                statusId = transaction?.data?.backofficePending ? "AWAITING" : transactionStatus.PENDING;
                break;
            case transactionStatus.FINISHED:
                statusId = "APPROVED";
                break;
            default:
                statusId = "REJECTED";
                break;
        }
        return statusId;
    };

    const getMdOffset = () => {
        if (transaction?.idTransactionStatus === transactionStatus.FINISHED || showSignButton) {
            return 2;
        }
        return 4;
    };

    return (
        <Fragment>
            <Notification scopeToShow="unsecured.loan" />
            {!isBackoffice && (
                <ResponsiveHeader
                    ticket
                    metaTitle={i18n.get(`unsecured.loans.${transactionData.loanSimulation.loanType}.title`, "")}
                />
            )}
            <MainContainer showLoader={isLoading}>
                <div className="container" ref={componentRef}>
                    <div ref={mobileRef}>
                        <TransactionCard transaction={transaction}>
                            {transaction.idTransactionStatus === transactionStatus.PENDING &&
                            transaction?.data?.backofficePending === true ? (
                                <div className="media-object  column">
                                    <div className="text-center">
                                        {isDesktop && <h1>{i18n.get("unsecured.loan.transaction.title", "")}</h1>}
                                        <h3>{i18n.get("unsecured.loan.transaction.status.PROCESSING", "")}</h3>
                                    </div>
                                    <div className="illustration text-center">
                                        <Image
                                            src="images/figma/illustrations/ticket-pending-approval.svg"
                                            className="svg-image logo"
                                        />
                                    </div>
                                </div>
                            ) : (
                                <TransactionHeader
                                    title={`unsecured.loans.${transactionData.loanSimulation.loanType}.title`}
                                    subTitle={`unsecured.loan.transaction.status.${transactionStatusID()}`}
                                    isDesktop
                                />
                            )}
                            <EcheqTransactionCard transaction={transaction}>
                                <div className="ticket__wrapper">
                                    <div className="ticket__header" />
                                    <div className="ticket__body">
                                        <div className="ticket-header-info  three-col-centered">
                                            <CustomData
                                                titleText={i18n.get("", "Fecha de Solicitud")}
                                                titleTextType="TEXT"
                                                valueTextType="TEXT"
                                                valueText={moment(transaction.creationDateTime).utc().format("DD/MM/YYYY")}
                                            />
                                            <CustomData
                                                titleText={i18n.get(
                                                    "unsecured.loans.confirm.debitCreditAccount",
                                                    "Cuenta de acreditación y débito",
                                                )}
                                                titleTextType="TEXT"
                                                valueTextType="TEXT"
                                                valueText={transactionData.loanSimulation.accountLabel}
                                            />
                                            <CustomData
                                                dataClass={`amount tag tag-${
                                                    isPending ? "warning" : "echeq-character"
                                                }`}
                                                titleText={i18n.get(
                                                    "unsecured.loans.confirm.detailLoan.amountToBeCredited",
                                                    "Monto a acreditar",
                                                )}
                                                titleTextType="TEXT"
                                                valueTextType="TEXT"
                                                valueText={
                                                    <FormattedAmount
                                                        currency="ARS"
                                                        quantity={numberUtils.formatAmountStrDouble(
                                                            transactionData.loanSimulation.totalOperationAmount,
                                                        )}
                                                    />
                                                }
                                            />
                                        </div>
                                        <div className="view-more-data-button" id="element-to-ignore">
                                            <div className="ticket__divider inside-ticket__body">
                                                <div className="ticket__notch ticket__notch--left"> </div>
                                                <div className="ticket__notch ticket__notch--right"> </div>
                                            </div>
                                            <Button
                                                aria-expanded={showMoreData ? "true" : "false"}
                                                className="more-button"
                                                label={buttonLabel}
                                                bsStyle="link"
                                                onClick={handleSeeMoreData}
                                                image={
                                                    showMoreData
                                                        ? "figma/icon/arrow-up.svg"
                                                        : "figma/icon/arrow-down.svg"
                                                }
                                            />

                                            <div
                                                className={`view-more-data full-width view-discounts-echeq ${
                                                    showMoreData ? "expand " : "collapse"
                                                }`}>
                                                <h2 className="col-span-full">
                                                    {i18n.get("unsecured.loans.confirm.detailLoan.title")}
                                                </h2>
                                                <GridTable className="equivalent-total-balance">
                                                    <GridTable.Container columnsTemplate="fixed" columns={3}>
                                                        <GridTable.Data rowStart={1} columnStart={1}>
                                                            <TransactionInfo
                                                                fieldString="N° de Préstamo"
                                                                value={transactionData.loanNumber}
                                                            />
                                                        </GridTable.Data>
                                                        <GridTable.Data
                                                            rowStart={isDesktop ? 1 : 2}
                                                            columnStart={isDesktop ? 2 : 1}>
                                                            <TransactionInfo
                                                                fieldString="Monto solicitado"
                                                                value={
                                                                    <FormattedAmount
                                                                        currency="ARS"
                                                                        quantity={
                                                                            transactionData.loanSimulation
                                                                                .totalOperationAmount
                                                                        }
                                                                    />
                                                                }
                                                            />
                                                        </GridTable.Data>
                                                        <GridTable.Data
                                                            rowStart={isDesktop ? 1 : 3}
                                                            columnStart={isDesktop ? 3 : 1}>
                                                            <TransactionInfo
                                                                fieldString="Duración del préstamo"
                                                                value={transactionData.loanSimulation.duration}
                                                            />
                                                        </GridTable.Data>
                                                        <GridTable.Data rowStart={isDesktop ? 2 : 4} columnStart={1}>
                                                            <TransactionInfo
                                                                fieldString="Sistema de amortización"
                                                                value={i18n.get(
                                                                    `unsecured.loans.types.${transactionData.loanSimulation.subproductId}`,
                                                                )}
                                                            />
                                                        </GridTable.Data>
                                                        <GridTable.Data
                                                            rowStart={isDesktop ? 2 : 4}
                                                            columnStart={isDesktop ? 2 : 1}>
                                                            <TransactionInfo
                                                                fieldString="Fecha de vencimiento de cuotas"
                                                                value={transactionData.loanSimulation.paymentDate}
                                                            />
                                                        </GridTable.Data>
                                                        <GridTable.Data
                                                            rowStart={isDesktop ? 2 : 5}
                                                            columnStart={isDesktop ? 3 : 1}>
                                                            <TransactionInfo
                                                                fieldString="Cantidad de cuotas"
                                                                value={
                                                                    transactionData.loanSimulation.installmentList
                                                                        ?.length
                                                                }
                                                            />
                                                        </GridTable.Data>
                                                        <GridTable.Data rowStart={4} columnStart={1}>
                                                            <TransactionInfo
                                                                fieldString="Primera cuota"
                                                                value={
                                                                    <FormattedAmount
                                                                        currency="ARS"
                                                                        quantity={
                                                                            transactionData.loanSimulation
                                                                                .firstPureInstallment
                                                                        }
                                                                    />
                                                                }
                                                            />
                                                        </GridTable.Data>
                                                        <GridTable.Data
                                                            rowStart={isDesktop ? 4 : 6}
                                                            columnStart={isDesktop ? 2 : 1}>
                                                            <TransactionInfo
                                                                fieldString="Última cuota"
                                                                value={
                                                                    <FormattedAmount
                                                                        currency="ARS"
                                                                        quantity={
                                                                            transactionData.loanSimulation
                                                                                .lastPureInstallment
                                                                        }
                                                                    />
                                                                }
                                                            />
                                                        </GridTable.Data>
                                                        <GridTable.Data
                                                            rowStart={isDesktop ? 4 : 6}
                                                            columnStart={isDesktop ? 3 : 1}>
                                                            <TransactionInfo
                                                                fieldString="Comisiones por otorgamiento del Préstamo"
                                                                value={
                                                                    <FormattedAmount
                                                                        currency="ARS"
                                                                        quantity={
                                                                            transactionData.loanSimulation.commission
                                                                        }
                                                                    />
                                                                }
                                                            />
                                                        </GridTable.Data>
                                                    </GridTable.Container>
                                                </GridTable>
                                                <div className="ticket__divider inside-ticket__body" />
                                                <h2 className="col-span-full">
                                                    {i18n.get("unsecured.loans.simulation.rate.subtitle")}
                                                </h2>
                                                <GridTable className="equivalent-total-balance">
                                                    <GridTable.Container columnsTemplate="fixed" columns={3}>
                                                        <GridTable.Data rowStart={1} columnStart={1}>
                                                            <TransactionInfo
                                                                fieldString="Tipo de tasa"
                                                                value={transactionData.loanSimulation.rateType}
                                                            />
                                                        </GridTable.Data>
                                                        <GridTable.Data
                                                            rowStart={isDesktop ? 1 : 2}
                                                            columnStart={isDesktop ? 2 : 1}>
                                                            <TransactionInfo
                                                                fieldString="Tasa nominal anual (TNA)"
                                                                value={transactionData.loanSimulation.tna}
                                                            />
                                                        </GridTable.Data>
                                                        <GridTable.Data
                                                            rowStart={isDesktop ? 1 : 3}
                                                            columnStart={isDesktop ? 3 : 1}>
                                                            <TransactionInfo
                                                                fieldString="Tasa efectiva anual (TEA)"
                                                                value={transactionData.loanSimulation.tea}
                                                            />
                                                        </GridTable.Data>
                                                        <GridTable.Data rowStart={isDesktop ? 2 : 4} columnStart={1}>
                                                            <TransactionInfo
                                                                fieldString="Costo Financiero Total Efectivo Anual (CFTEA)"
                                                                value={transactionData.loanSimulation.cftSIVA}
                                                            />
                                                        </GridTable.Data>
                                                        <GridTable.Data
                                                            rowStart={isDesktop ? 2 : 5}
                                                            columnStart={isDesktop ? 2 : 1}>
                                                            <TransactionInfo
                                                                fieldString="CFTEA + IVA"
                                                                value={transactionData.loanSimulation.cftCIVA}
                                                            />
                                                        </GridTable.Data>
                                                    </GridTable.Container>
                                                </GridTable>
                                                <div className="ticket__divider inside-ticket__body" />
                                                <h2 className="col-span-full">
                                                    {i18n.get("unsecured.loans.confirm.retentions.title")}
                                                </h2>
                                                <GridTable className="equivalent-total-balance">
                                                    <GridTable.Container columnsTemplate="fixed" columns={3}>
                                                        <GridTable.Data rowStart={1} columnStart={1}>
                                                            <TransactionInfo
                                                                fieldString="Percepción de IIBB"
                                                                value={
                                                                    <FormattedAmount
                                                                        currency="ARS"
                                                                        quantity={numberUtils.formatAmountStrDouble(
                                                                            transactionData.loanSimulation.charges
                                                                                ?.iibbPerception,
                                                                        )}
                                                                    />
                                                                }
                                                            />
                                                        </GridTable.Data>
                                                        <GridTable.Data
                                                            rowStart={isDesktop ? 1 : 2}
                                                            columnStart={isDesktop ? 2 : 1}>
                                                            <TransactionInfo
                                                                fieldString="IVA"
                                                                value={
                                                                    <FormattedAmount
                                                                        currency="ARS"
                                                                        quantity={numberUtils.formatAmountStrDouble(
                                                                            transactionData.loanSimulation.charges?.iva,
                                                                        )}
                                                                    />
                                                                }
                                                            />
                                                        </GridTable.Data>
                                                        <GridTable.Data
                                                            rowStart={isDesktop ? 1 : 3}
                                                            columnStart={isDesktop ? 3 : 1}>
                                                            <TransactionInfo
                                                                fieldString="Percepción IVA"
                                                                value={
                                                                    <FormattedAmount
                                                                        currency="ARS"
                                                                        quantity={numberUtils.formatAmountStrDouble(
                                                                            transactionData.loanSimulation.charges
                                                                                ?.ivaPerception,
                                                                        )}
                                                                    />
                                                                }
                                                            />
                                                        </GridTable.Data>
                                                        <GridTable.Data rowStart={isDesktop ? 2 : 4} columnStart={1}>
                                                            <TransactionInfo
                                                                fieldString="Jurisdicción de IIBB"
                                                                value={
                                                                    transactionData.loanSimulation.charges?.jurisdiction
                                                                }
                                                            />
                                                        </GridTable.Data>
                                                    </GridTable.Container>
                                                </GridTable>
                                            </div>
                                        </div>

                                        <div className="view-more-data-button" id="element-to-ignore">
                                            <div className="ticket__divider inside-ticket__body">
                                                <div className="ticket__notch ticket__notch--left"> </div>
                                                <div className="ticket__notch ticket__notch--right"> </div>
                                            </div>
                                            <Button
                                                aria-expanded={showInstallments ? "true" : "false"}
                                                className="more-button"
                                                label="unsecured.loans.simulation.installment.show.detail"
                                                bsStyle="link"
                                                onClick={handleSeeInstallment}
                                                image={
                                                    showInstallments
                                                        ? "figma/icon/arrow-up.svg"
                                                        : "figma/icon/arrow-down.svg"
                                                }
                                            />
                                            <div
                                                className={`view-more-data view-discounts-echeq ${
                                                    showInstallments ? "expand " : "collapse"
                                                }`}>
                                                <h2>
                                                    <I18n id="loans.detail.title" component="h2" />
                                                </h2>

                                                <GridTable className="grid-table echeck-table tablelist col-span-full">
                                                    {isDesktop && (
                                                        <GridTable.Container
                                                            columns={7}
                                                            rows={1}
                                                            className="received-from">
                                                            <GridTable.Data columnStart={1} rowStart={1}>
                                                                <CustomText
                                                                    className="title-discounts"
                                                                    text={i18n.get(
                                                                        "unsecured.loans.confirm.Installment",
                                                                        "Cuota",
                                                                    )}
                                                                />
                                                            </GridTable.Data>
                                                            <GridTable.Data columnStart={2} rowStart={1}>
                                                                <CustomText
                                                                    className="title-discounts"
                                                                    text={i18n.get(
                                                                        "unsecured.loans.confirm.expirationDate",
                                                                        "Fecha de vencimiento",
                                                                    )}
                                                                />
                                                            </GridTable.Data>
                                                            <GridTable.Data columnStart={3} rowStart={1}>
                                                                <CustomText
                                                                    className="title-discounts"
                                                                    text={i18n.get(
                                                                        "unsecured.loans.confirm.interests",
                                                                        "Intereses",
                                                                    )}
                                                                />
                                                            </GridTable.Data>
                                                            <GridTable.Data columnStart={4} rowStart={1}>
                                                                <CustomText
                                                                    className="title-discounts"
                                                                    text={i18n.get(
                                                                        "loans.amortization.label",
                                                                        "Amortización",
                                                                    )}
                                                                />
                                                            </GridTable.Data>
                                                            <GridTable.Data columnStart={5} rowStart={1}>
                                                                <CustomText
                                                                    className="title-discounts"
                                                                    text={i18n.get("loans.iva.label", "IVA")}
                                                                />
                                                            </GridTable.Data>
                                                            <GridTable.Data columnStart={6} rowStart={1}>
                                                                <CustomText
                                                                    className="title-discounts"
                                                                    text={i18n.get("loans.stamps.label", "Sellos")}
                                                                />
                                                            </GridTable.Data>
                                                            <GridTable.Data columnStart={7} rowStart={1}>
                                                                <CustomText
                                                                    className="title-discounts"
                                                                    text={i18n.get(
                                                                        "unsecured.loans.common.form.fields.amount.label",
                                                                        "Monto",
                                                                    )}
                                                                />
                                                            </GridTable.Data>
                                                        </GridTable.Container>
                                                    )}

                                                    <Spring
                                                        config={config.stiff}
                                                        from={{ height: 0 }}
                                                        to={{
                                                            height: transactionData.loanSimulation.installmentList
                                                                ?.length
                                                                ? "auto"
                                                                : 0,
                                                            overflow: "hidden",
                                                        }}>
                                                        {(styles) => (
                                                            <div className="form-group" style={styles}>
                                                                <div className="form-group-control-list">
                                                                    {transactionData.loanSimulation.installmentList?.map(
                                                                        (row, index) => (
                                                                            <GridTable.Container
                                                                                columns={7}
                                                                                rows={1}
                                                                                className="received-from data-echeq"
                                                                                key={index}>
                                                                                <GridTable.Data
                                                                                    rowStart={1}
                                                                                    columnStart={1}
                                                                                    flexDirection="column">
                                                                                    {isDesktop ? (
                                                                                        <CustomText text={row.number} />
                                                                                    ) : (
                                                                                        <CustomData
                                                                                            titleText="unsecured.loans.confirm.Installment"
                                                                                            titleTextType="I18N"
                                                                                            valueText={row.number}
                                                                                        />
                                                                                    )}
                                                                                </GridTable.Data>

                                                                                <GridTable.Data
                                                                                    rowStart={1}
                                                                                    columnStart={2}>
                                                                                    {isDesktop ? (
                                                                                        <CustomText
                                                                                            text={row.paymentDueDate}
                                                                                        />
                                                                                    ) : (
                                                                                        <CustomData
                                                                                            titleText="unsecured.loans.confirm.expirationDate"
                                                                                            titleTextType="I18N"
                                                                                            valueText={
                                                                                                row.paymentDueDate
                                                                                            }
                                                                                        />
                                                                                    )}
                                                                                </GridTable.Data>

                                                                                <GridTable.Data
                                                                                    rowStart={1}
                                                                                    columnStart={3}>
                                                                                    {isDesktop ? (
                                                                                        <CustomText
                                                                                            text={
                                                                                                <FormattedAmount
                                                                                                    currency="ARS"
                                                                                                    quantity={numberUtils.formatAmountStrDouble(
                                                                                                        row.installmentInterestDue,
                                                                                                    )}
                                                                                                />
                                                                                            }
                                                                                        />
                                                                                    ) : (
                                                                                        <CustomData
                                                                                            titleText="unsecured.loans.confirm.interests"
                                                                                            titleTextType="I18N"
                                                                                            valueText={
                                                                                                <FormattedAmount
                                                                                                    currency="ARS"
                                                                                                    quantity={numberUtils.formatAmountStrDouble(
                                                                                                        row.installmentInterestDue,
                                                                                                    )}
                                                                                                />
                                                                                            }
                                                                                        />
                                                                                    )}
                                                                                </GridTable.Data>

                                                                                <GridTable.Data
                                                                                    rowStart={1}
                                                                                    columnStart={4}>
                                                                                    {isDesktop ? (
                                                                                        <CustomText
                                                                                            text={
                                                                                                <FormattedAmount
                                                                                                    currency="ARS"
                                                                                                    quantity={numberUtils.formatAmountStrDouble(
                                                                                                        row.capital,
                                                                                                    )}
                                                                                                />
                                                                                            }
                                                                                        />
                                                                                    ) : (
                                                                                        <CustomData
                                                                                            titleText="loans.amortization.label"
                                                                                            titleTextType="I18N"
                                                                                            valueText={
                                                                                                <FormattedAmount
                                                                                                    currency="ARS"
                                                                                                    quantity={numberUtils.formatAmountStrDouble(
                                                                                                        row.capital,
                                                                                                    )}
                                                                                                />
                                                                                            }
                                                                                        />
                                                                                    )}
                                                                                </GridTable.Data>

                                                                                <GridTable.Data
                                                                                    rowStart={1}
                                                                                    columnStart={5}>
                                                                                    {isDesktop ? (
                                                                                        <CustomText
                                                                                            text={
                                                                                                <FormattedAmount
                                                                                                    currency="ARS"
                                                                                                    quantity={numberUtils.formatAmountStrDouble(
                                                                                                        row.iva,
                                                                                                    )}
                                                                                                />
                                                                                            }
                                                                                        />
                                                                                    ) : (
                                                                                        <CustomData
                                                                                            titleText="loans.iva.label"
                                                                                            titleTextType="I18N"
                                                                                            valueText={
                                                                                                <FormattedAmount
                                                                                                    currency="ARS"
                                                                                                    quantity={numberUtils.formatAmountStrDouble(
                                                                                                        row.iva,
                                                                                                    )}
                                                                                                />
                                                                                            }
                                                                                        />
                                                                                    )}
                                                                                </GridTable.Data>

                                                                                <GridTable.Data
                                                                                    rowStart={1}
                                                                                    columnStart={6}>
                                                                                    {isDesktop ? (
                                                                                        <CustomText
                                                                                            text={
                                                                                                <FormattedAmount
                                                                                                    currency="ARS"
                                                                                                    quantity={numberUtils.formatAmountStrDouble(
                                                                                                        row.stampTax,
                                                                                                    )}
                                                                                                />
                                                                                            }
                                                                                        />
                                                                                    ) : (
                                                                                        <CustomData
                                                                                            titleText="loans.stamps.label"
                                                                                            titleTextType="I18N"
                                                                                            valueText={
                                                                                                <FormattedAmount
                                                                                                    currency="ARS"
                                                                                                    quantity={numberUtils.formatAmountStrDouble(
                                                                                                        row.stampTax,
                                                                                                    )}
                                                                                                />
                                                                                            }
                                                                                        />
                                                                                    )}
                                                                                </GridTable.Data>

                                                                                <GridTable.Data
                                                                                    rowStart={1}
                                                                                    columnStart={7}>
                                                                                    {isDesktop ? (
                                                                                        <CustomText
                                                                                            text={
                                                                                                <FormattedAmount
                                                                                                    currency="ARS"
                                                                                                    quantity={numberUtils.formatAmountStrDouble(
                                                                                                        row.totalFee,
                                                                                                    )}
                                                                                                />
                                                                                            }
                                                                                        />
                                                                                    ) : (
                                                                                        <CustomData
                                                                                            titleText="unsecured.loans.common.form.fields.amount.label"
                                                                                            titleTextType="I18N"
                                                                                            valueText={
                                                                                                <FormattedAmount
                                                                                                    currency="ARS"
                                                                                                    quantity={numberUtils.formatAmountStrDouble(
                                                                                                        row.totalFee,
                                                                                                    )}
                                                                                                />
                                                                                            }
                                                                                        />
                                                                                    )}
                                                                                </GridTable.Data>
                                                                            </GridTable.Container>
                                                                        ),
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </Spring>
                                                </GridTable>
                                                <TransactionSign
                                                    hasVaucher
                                                    isDesktop={isDesktop}
                                                    noDate
                                                    isSmall
                                                    creationTime
                                                    showMoreData={showMoreData}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <Row className="ticket__footer" data-copytext={i18n.get("global.ticketCopyText")} />
                                </div>
                            </EcheqTransactionCard>
                            <div className="info-ticket">
                                {transaction.idTransactionStatus === transactionStatus.PENDING && (
                                    <div className="container--alert" role="alert">
                                        <div className="alert alert-info">
                                            <CustomData
                                                icon="information_fill.svg"
                                                titleText={i18n.get(
                                                    "",
                                                    "Recordá de completar el esquema de firmas ya que la validez de la simulación tiene vigencia hasta las 18hs del día de hoy.",
                                                )}
                                                keepSpaceIfNoValue={false}
                                            />
                                        </div>
                                        <div style={{ paddingBottom: "10px" }} />
                                    </div>
                                )}
                            </div>
                        </TransactionCard>
                    </div>
                    {!isBackoffice && (
                        <Row className="form-actions">
                            <Col xs={12} md={4} mdOffset={getMdOffset()}>
                                <CustomButton
                                    func={() => dispatch(push("/desktop"))}
                                    text="forms.transaction.ticket.actions.backToHome"
                                    textType="I18N"
                                    className="btn btn-outline"
                                />
                            </Col>
                            {transaction.idTransactionStatus === transactionStatus.FINISHED ? (
                                <>
                                    <Col xs={12} md={4}>
                                        <Button
                                            className="btn btn-outline"
                                            image="images/figma/icon/download.svg"
                                            label="global.download"
                                            onClick={handleDownload}
                                        />
                                    </Col>
                                </>
                            ) : (
                                <>{showSignButton && sign()}</>
                            )}
                            {!showSignButton && !isDesktop && (
                                <Col xs={12} md={4}>
                                    <Button
                                        className="btn btn-outline"
                                        image="images/figma/icon/share.svg"
                                        label="global.share"
                                        onClick={() => {
                                            handleShareMobile();
                                        }}
                                    />
                                </Col>
                            )}
                        </Row>
                    )}
                </div>
            </MainContainer>
        </Fragment>
    );
};

UnsecuredLoanTransactionPage.propTypes = {
    isDesktop: bool.isRequired,
    transaction: shape({}).isRequired,
    fetching: bool.isRequired,
    isCancellingTransaction: bool.isRequired,
    idTransaction: string.isRequired,
};

const mapStateToProps = (state) => ({
    fetching: loansSelectors.getFetching(state),
    transaction: formSelectors.getTransaction(state),
    loanApplicationInProgress: loansSelectors.getLoanApplicationInProgress(state),
    isCancellingTransaction: formSelectors.getIsCancellingTransaction(state),
    idTransaction: formSelectors.getTransaction(state).idTransaction,
});

export default compose(
    connect(mapStateToProps),
    withFormik({
        validateOnChange: false,
        validateOnBlur: false,
        mapPropsToValues: (props) => ({
            id: props.match.params.id,
            transaction: props.transaction,
            isCancellingTransaction: props.isCancellingTransaction,
            credential: "",
            password: "",
        }),
        validationSchema: (props) =>
            Yup.object().shape({
                credential: !props.isCancellingTransaction
                    ? Yup.string().required(i18n.get("deposits.request.required"))
                    : Yup.string().notRequired(),
                password: props.isCancellingTransaction
                    ? Yup.string().required(i18n.get("deposits.request.required"))
                    : Yup.string().notRequired(),
            }),
        handleSubmit: ({ credential, password }, formikBag) => {
            const { dispatch, transaction, isCancellingTransaction } = formikBag.props;

            const params = {
                loanSimulation: transaction.data.loanSimulation,
                amount: transaction.data.amount,
                _otp: credential,
            };

            if (isCancellingTransaction) {
                dispatch(formActions.cancelTransaction({ credentials: { password, otp: "" }, formikBag }));
            } else {
                dispatch(loansActions.unsecuredLoansApplicationSend(params));
            }
        },
    }),
)(UnsecuredLoanTransactionPage);
